"use client";

import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { Dialog, DialogContent, DialogHeaderCentered } from "@shared/src/components/ui/dialog";
import { Button } from "@shared/src/components/ui/button";
import { FaArrowRight, FaArrowUpRightFromSquare } from "react-icons/fa6";
import { protectedAxios } from "@shared/src/utils/axios";
import { useRouter } from "next/navigation";
import { useModalStore } from "@shared/src/stores/useModalStore";
import { chains } from "@shared/src/config/chains";
import { TokenChip } from "@shared/src/components/ui/token-chip";
import { NetworkChip } from "@shared/src/components/ui/network-chip";
import { useP2PConfig } from "@shared/src/hooks/useP2PConfig";
import { faucetService } from "@shared/src/services/faucet.service";
import { useAllBalances } from "@shared/src/hooks/useAllBalances";
import { NativeFaucetModal } from "@shared/src/components/common/dialogs/NativeFaucetModal";
import { IS_MAINNET } from "@shared/src/constants";

interface FaucetModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface FaucetClaimInfo {
  id?: number;
  address?: string;
  createdAt?: string;
}

export function Faucet({ open, onOpenChange }: FaucetModalProps) {
  const router = useRouter();
  const { tokens } = useP2PConfig({ tokens: true });
  const [claimInfo, setClaimInfo] = useState<FaucetClaimInfo | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<string>("");
  const [canClaim, setCanClaim] = useState<boolean>(true);
  const { showModal, closeModal } = useModalStore();
  const { refetch } = useAllBalances({});
  const [showNativeFaucet, setShowNativeFaucet] = useState(false);

  // Fetch the user's claim status
  useEffect(() => {
    if (open) {
      fetchClaimStatus();
    }
  }, [open]);

  // Calculate time remaining if user has claimed before
  useEffect(() => {
    console.log(claimInfo);
    if (!claimInfo?.createdAt) {
      setCanClaim(true);
      return;
    }

    const updateTimeRemaining = () => {
      const lastClaim = new Date(claimInfo.createdAt as string);
      const nextClaimTime = new Date(lastClaim.getTime() + 24 * 60 * 60 * 1000); // Add 24 hours
      const now = new Date();

      // If next claim time is in the future, show countdown
      if (nextClaimTime > now) {
        const diffMs = nextClaimTime.getTime() - now.getTime();
        const hours = Math.floor(diffMs / (1000 * 60 * 60));
        const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);

        setTimeRemaining(
          `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds
            .toString()
            .padStart(2, "0")}`,
        );
        setCanClaim(false);
      } else {
        setTimeRemaining("");
        setCanClaim(true);
      }
    };

    // Update immediately and then every second
    updateTimeRemaining();
    const interval = setInterval(updateTimeRemaining, 1000);

    return () => clearInterval(interval);
  }, [claimInfo]);

  const fetchClaimStatus = async () => {
    try {
      const { data } = await faucetService.getFaucets();
      setClaimInfo(data || null);
    } catch (error) {
      console.error("Error fetching claim status:", error);
    }
  };

  const handleClaimAll = async () => {
    try {
      // Show loading modal
      showModal({
        type: "loading",
        title: "Claiming Tokens",
        message: "Please wait while we process your claim...",
        preventClose: true,
      });

      // Make the POST request to claim tokens
      await protectedAxios.post("/auth/faucet");

      // Hide loading and show success
      closeModal();
      onOpenChange(false);
      refetch();
      showModal({
        type: "success",
        title: "Tokens Claimed!",
        messageTitle: "Tokens Successfully Claimed",
        description: "Your tokens have been successfully added to your wallet.",
        buttonText: "Sell Some Tokens",
        buttonClick: async () => {
          handleSellTokens();
        },
      });

      // Refresh claim status
      fetchClaimStatus();
    } catch (error) {
      console.error("Error claiming tokens:", error);
      closeModal();
      showModal({
        type: "error",
        title: "Claim Failed",
        message: "There was an error claiming your tokens. Please try again later.",
        buttonText: "Close",
        buttonClick: async () => {
          closeModal();
        },
      });
    }
  };

  const handleSellTokens = () => {
    onOpenChange(false);
    closeModal();
    router.push("/?type=sell");
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          size="lg"
          background="primary"
          padding="md"
          position="center-mobile-bottom"
          hideCloseButton={true}
          height="80vh"
          layout="flex-col-overflow"
          className="space-y-4 gap-0 md:h-auto max-h-[90vh]"
        >
          <DialogHeaderCentered
            title="Available Tokens"
            description="Claim the number of Tokens you have been allocated below."
            onClose={() => onOpenChange(false)}
          />

          {!canClaim && timeRemaining && (
            <div className="mt-2 p-3 bg-surfaces-bg-secondary rounded-lg">
              <p className="text-text-error font-medium">
                Next claim available in: <span className="font-bold">{timeRemaining}</span>
              </p>
            </div>
          )}

          <div className="flex-1 min-h-0 custom-scrollbar overflow-y-scroll">
            <div className="flex flex-col gap-2">
              {tokens ? (
                tokens.map((token, index) => {
                  const network = chains.find(chain => chain.id === token.networkId);
                  return (
                    <div
                      key={token.address || index}
                      className="relative rounded-2xl p-6 bg-surfaces-bg-secondary flex justify-center items-center gap-3"
                    >
                      <h6 className="font-bold text-text-primary">1000 </h6>
                      <div className="flex items-center gap-2 ">
                        <TokenChip
                          iconUrl={token.icon}
                          tokenAddress={token.address}
                          showCopy={true}
                          networkId={token.networkId}
                        />
                        <div className="relative w-fit">
                          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center size-6 rounded-xl bg-surfaces-bg-secondary-darker border-2 border-border-secondary z-10 overflow-visible">
                            <FaArrowRight size={12} className="text-text-primary" />
                          </div>
                        </div>
                        <NetworkChip networkId={network?.id} showDropdownIcon={false} className="flex-1 min-w-0" />
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="col-span-2 text-center py-8 text-text-secondary">No tokens available</div>
              )}
            </div>
          </div>

          <div className="shrink-0 py-4 space-y-2">
            <Button
              type="button"
              variant="tertiary"
              size="md"
              radius="full"
              width="full"
              onClick={handleClaimAll}
              disabled={!canClaim || !tokens}
            >
              {canClaim ? "Claim all" : "Wait for next claim"}
              {canClaim && <FaArrowRight size={20} className="ml-2" />}
            </Button>
            <div className="flex items-center gap-2 w-full">
              <div className="flex-1 h-px bg-slate-600"></div>
              <span className="text-text-secondary text-xs font-medium">or</span>
              <div className="flex-1 h-px bg-slate-600"></div>
            </div>
            <span className="text-text-secondary w-full flex justify-center text-xs font-medium">
              Need native tokens for gas fees?
            </span>

            <Button
              type="button"
              variant="surface-secondary"
              size="md"
              radius="full"
              width="full"
              className="text-small-2"
              onClick={() => {
                setTimeout(() => {
                  console.log("native modal open");
                  setShowNativeFaucet(true);
                }, 100);
                onOpenChange(false);
              }}
            >
              Go to Native Faucet
              <FaArrowUpRightFromSquare size={20} className="ml-2" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <NativeFaucetModal open={showNativeFaucet} onOpenChange={setShowNativeFaucet} />
    </>
  );
}

export function FaucetModal({ open, onOpenChange }: FaucetModalProps) {
  if (!IS_MAINNET) return <Faucet open={open} onOpenChange={onOpenChange} />;
  else return null;
}
